import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Box, Paper, Typography, TextField, IconButton, Stack, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import Iconify from '../../components/Iconify';
// hooks
import useLocales from '../../hooks/useLocales';
// utils
import { axiosInstance } from '../../utils/axios';

// ----------------------------------------------------------------------

// Written for LTR; the app's global stylis-plugin-rtl (see ThemeRtlLayout)
// automatically mirrors this to the left edge when the language is Arabic.
// Do not manually flip left/right here — that would double-mirror.
const WindowContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 160,
  right: 20,
  width: 380,
  height: 520,
  maxHeight: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.modal,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: theme.shadows[20],
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 32px)',
    height: 'calc(100vh - 230px)',
    bottom: 150,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
}));

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

// ----------------------------------------------------------------------

AIInsightsWindow.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function AIInsightsWindow({ open, onClose }) {
  const { translate, currentLang } = useLocales();
  const isRtl = currentLang.direction === 'rtl';
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const textInputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => textInputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSend = async () => {
    const question = inputValue.trim();
    if (!question || isSending) return;
    setInputValue('');
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setIsSending(true);
    try {
      const { data } = await axiosInstance.post('/dashboard/ask', {
        question,
        lang: currentLang.value,
        history,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: data.answer }]);
    } catch (error) {
      setMessages((prev) => [...prev, { role: 'assistant', content: translate('aiInsights.error') }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) return null;

  return (
    <WindowContainer>
      <Header>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="solar:magic-stick-3-bold-duotone" width={24} height={24} />
          <Typography variant="subtitle1" fontWeight="bold">
            {translate('aiInsights.title')}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <Iconify icon="eva:close-fill" width={20} height={20} />
        </IconButton>
      </Header>

      <MessagesContainer>
        {messages.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {translate('aiInsights.welcome')}
            </Typography>
          </Box>
        )}
        {messages.map((msg, index) => {
          const isUser = msg.role === 'user';
          return (
            <Box
              key={index}
              sx={{
                // flex-start/flex-end already follow the container's direction natively
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                bgcolor: isUser ? 'primary.main' : 'background.neutral',
                color: isUser ? 'primary.contrastText' : 'text.primary',
                px: 2,
                py: 1,
                borderRadius: 2,
                maxWidth: '85%',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {msg.content}
              </Typography>
            </Box>
          );
        })}
        {isSending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {translate('aiInsights.thinking')}
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <TextField
          fullWidth
          size="small"
          placeholder={translate('aiInsights.placeholder')}
          value={inputValue}
          inputRef={textInputRef}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton
          color="secondary"
          onClick={handleSend}
          disabled={!inputValue.trim() || isSending}
          sx={{ flexShrink: 0 }}
        >
          {isSending ? (
            <CircularProgress size={20} />
          ) : (
            <Iconify icon="ic:baseline-send" width={24} height={24} sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
          )}
        </IconButton>
      </InputContainer>
    </WindowContainer>
  );
}
